import React, { useEffect, useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Grid } from '@material-ui/core';
import { ArrowBack, Refresh, ExitToApp } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { HubConnectionBuilder, LogLevel } from '@aspnet/signalr';
import {
  formatDate,
  getTime,
  getTaskTypeName,
  asyncFetch,
  sortByDate,
  getAppData,
} from '../../helperFunctions';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import {
  setNotifications,
  setUsedMaterialsDialog,
  setMaterialsDialog,
  setShowTaskDetails,
  setData,
  setTaskUpdated,
  setTaskStarted,
  setMaterialSellDialog,
  setReturnMaterialsDialog,
} from '../redux';

import { getLogoutUrlRequest } from '../../ReactAdminOss._Core/src/reducers/Auth/actions';
import NotificationButton from './NotificationButton';
import { chatConfigured, GeneralChatButton } from '../../Chat';

export default () => {
  const { width } = useWindowDimensions();
  const dispatch = useDispatch();
  const fontSize = width <= 500 ? '4vw' : '20px';
  const appButtonFontSize = width <= 500 ? '2.3vw' : '12px';
  const data = useSelector(state => state.tasks.data);
  const notifications = useSelector(state => state.tasks.notifications);
  const showTaskDetails = useSelector(state => state.tasks.showTaskDetails);
  const [signalrConnection, setSignalrConnection] = useState({
    connection: null,
    mustReconnect: false,
  });
  const [appDataLoading, setAppDataLoading] = useState(false);
  const settings = useSelector(state => state.settings);

  const refreshAppData = async () => {
    setAppDataLoading(true);
    await getAppData({ data, dispatch });
    setAppDataLoading(false);
  };

  const buttonStyle = {
    fontSize: appButtonFontSize,
    padding: '5px',
    margin: '3px',
    minWidth: '0px',
  };

  useEffect(() => {
    if (!signalrConnection.connection || signalrConnection.mustReconnect) {
      const connection = new HubConnectionBuilder()
        .configureLogging(LogLevel.None)
        .withUrl(`${process.env.REACT_APP_TECHNICIANHUB}`)
        .build();

      connection
        .start()
        .then(
          () => setSignalrConnection({ connection, mustReconnect: false }),
          () =>
            setTimeout(
              () => setSignalrConnection({ ...signalrConnection, mustReconnect: true }),
              3000
            )
        )
        .catch(() => true);

      connection.onclose(() => setSignalrConnection({ ...signalrConnection, mustReconnect: true }));
    }

    return () => {
      signalrConnection.connection && signalrConnection.connection.stop();
    };
  }, [signalrConnection]);

  useEffect(() => {
    signalrConnection.connection &&
      signalrConnection.connection.connectionState === 1 &&
      signalrConnection.connection.on('TaskUpdated', payload => {
        const identicalTechnicians =
          !!data.scheduleByDate &&
          JSON.stringify(payload.technicians) === JSON.stringify(data.scheduleByDate.technicians);
        const currentTasks = data.tasks.filter(x => payload.taskIds.includes(x.id));
        const notificationsList = [...notifications.list];
        const newTasks = [...data.tasks];

        const deleteTask = taskToDelete => {
          const backgroundColor = taskToDelete.type === 3 ? '#da291c' : '#a5daff';
          const taskType = `${taskToDelete.ticketTypeName ||
            getTaskTypeName(taskToDelete.type) ||
            taskToDelete.manualTicketType}`;

          newTasks.splice(data.tasks.indexOf(taskToDelete), 1);
          notificationsList.push({
            key: notificationsList.length,
            changeTypeText: 'Изтрита е задача',
            time: `${getTime(taskToDelete.start)} - ${getTime(taskToDelete.end)}`,
            taskType,
            backgroundColor,
            changes: [],
          });
        };

        if (!identicalTechnicians && currentTasks.length > 0) {
          for (let i = 0; i < currentTasks.length; i++) {
            // задачата е преместена в друг график
            deleteTask(currentTasks[i]);
          }

          dispatch(setNotifications({ ...notifications, list: notificationsList, seen: false }));
          dispatch(setData({ ...data, tasks: newTasks }));
        }

        if (identicalTechnicians) {
          (async () => {
            const updatedTasks = await asyncFetch(
              `${process.env.REACT_APP_TECHNICIANS_API}/Tasks/GetTasks`,
              {
                method: 'POST',
                mode: 'cors',
                referrerPolicy: 'strict-origin-when-cross-origin',
                body: JSON.stringify(payload.taskIds),
                headers: {
                  'Content-Type': 'application/json',
                  authorization: `OSS ${localStorage.getItem('OSS_Token')}`,
                },
              },
              undefined,
              undefined,
              dispatch
            );

            if (updatedTasks) {
              for (let i = 0; i < updatedTasks.length; i++) {
                const updatedTask = updatedTasks[i];
                const taskToBeUpdated = currentTasks.find(x => x.id === updatedTask.id);
                if (taskToBeUpdated) {
                  const backgroundColor = taskToBeUpdated.type === 3 ? '#da291c' : '#a5daff';
                  const taskType =
                    taskToBeUpdated.ticketTypeName ||
                    getTaskTypeName(taskToBeUpdated.type) ||
                    taskToBeUpdated.manualTicketType;
                  const changes = [];

                  // задачата е 'изтрита' от координатор (статус сменен на '-' през ббс2)
                  if (updatedTask.type === 1) {
                    deleteTask(taskToBeUpdated);
                  }

                  // промени по задача
                  if (
                    updatedTask.type !== 1 &&
                    JSON.stringify(updatedTask) !== JSON.stringify(taskToBeUpdated)
                  ) {
                    dispatch(setTaskUpdated({ taskId: taskToBeUpdated.id }));
                    if (
                      taskToBeUpdated.start.ticks !== updatedTask.start.ticks ||
                      taskToBeUpdated.end.ticks !== updatedTask.end.ticks
                    ) {
                      changes.push(
                        `Интервалът за изпълнение на задачата е променен: ${getTime(
                          taskToBeUpdated.start
                        )} - ${getTime(taskToBeUpdated.end)} -> ${getTime(
                          updatedTask.start
                        )} - ${getTime(updatedTask.end)}.`
                      );
                    }
                    if (taskToBeUpdated.address !== updatedTask.address) {
                      changes.push(
                        `Адресът е променен: ${taskToBeUpdated.address} -> ${updatedTask.address}.`
                      );
                    }
                    if (taskToBeUpdated.note !== updatedTask.note) {
                      changes.push(
                        `Забележката е променена: ${taskToBeUpdated.note} -> ${updatedTask.note}.`
                      );
                    }
                    if (taskToBeUpdated.client !== updatedTask.client) {
                      changes.push(
                        `Промяна на данните на клиента: ${taskToBeUpdated.client} -> ${updatedTask.client}.`
                      );
                    }
                    if (taskToBeUpdated.clientNo !== updatedTask.clientNo) {
                      changes.push(
                        `Промяна на данните на клиента, абонаментен номер: ${taskToBeUpdated.clientNo} -> ${updatedTask.clientNo}.`
                      );
                    }
                    if (taskToBeUpdated.phone !== updatedTask.phone) {
                      changes.push(
                        `Промяна на данните на клиента, телефонен номер: ${taskToBeUpdated.phone} -> ${updatedTask.phone}.`
                      );
                    }
                    // if (JSON.stringify(task.services) !== JSON.stringify(updatedTask.services)) {
                    //   changes.push(`Услугите са променени.`);
                    // }
                    if (
                      JSON.stringify(taskToBeUpdated.devices) !==
                      JSON.stringify(updatedTask.devices)
                    ) {
                      changes.push(`Устройствата са променени.`);
                    }

                    newTasks.splice(data.tasks.indexOf(taskToBeUpdated), 1, updatedTask);
                    notificationsList.push({
                      key: notificationsList.length,
                      changeTypeText: 'Има промени по задача',
                      time: `${getTime(taskToBeUpdated.start)} - ${getTime(taskToBeUpdated.end)}`,
                      taskType,
                      backgroundColor,
                      changes,
                    });
                  }
                } else {
                  // добавена е нова задача в графика
                  const backgroundColor = updatedTask.type === 3 ? '#da291c' : '#a5daff';
                  const taskType =
                    updatedTask.ticketTypeName ||
                    getTaskTypeName(updatedTask.type) ||
                    taskToBeUpdated?.manualTicketType;
                  newTasks.push(updatedTask);
                  notificationsList.push({
                    key: notificationsList.length,
                    changeTypeText: 'Добавена е нова задача',
                    time: `${getTime(updatedTask.start)} - ${getTime(updatedTask.end)}`,
                    taskType,
                    backgroundColor,
                    changes: [],
                  });
                }

                dispatch(
                  setNotifications({ ...notifications, list: notificationsList, seen: false })
                );
                newTasks.sort((a, b) => sortByDate(a, b));
                dispatch(setData({ ...data, tasks: newTasks }));
                const startedTasks = newTasks.filter(x => x.appStart && !x.appEnd);
                startedTasks.length > 0
                  ? dispatch(setTaskStarted({ started: true, taskId: startedTasks[0].id }))
                  : dispatch(setTaskStarted({ started: false, taskId: null }));
              }
            }
          })();
        }
      });

    return () => {
      if (signalrConnection.connection && signalrConnection.connection.connectionState === 1) {
        signalrConnection.connection.off('TaskUpdated');
      }
    };
  }, [signalrConnection, data, notifications, dispatch]);

  useEffect(() => {
    signalrConnection.connection &&
      signalrConnection.connection.connectionState === 1 &&
      signalrConnection.connection.on('RefreshApp', () => {
        window.location.reload();
      });
    return () => {
      if (signalrConnection.connection && signalrConnection.connection.connectionState === 1) {
        signalrConnection.connection.off('RefreshApp');
      }
    };
  }, [signalrConnection]);

  return (
    <AppBar style={{ position: 'inherit' }}>
      <Grid container>
        <Grid item>
          {data.scheduleByDate && data.scheduleByDate.title && (
            <Toolbar style={{ minHeight: width <= 500 ? '5vh' : '40px' }}>
              <div style={{ marginRight: '3px' }}>
                <Typography style={{ fontSize }}>График: {data.scheduleByDate.title}</Typography>
                <Typography style={{ fontSize }}>
                  Техници: {data.scheduleByDate.technicians[0]}
                </Typography>
                {data.scheduleByDate.technicians[1] && (
                  <Typography style={{ fontSize }}>{data.scheduleByDate.technicians[1]}</Typography>
                )}
                {data.scheduleByDate.technicians[2] && (
                  <Typography style={{ fontSize }}>{data.scheduleByDate.technicians[2]}</Typography>
                )}
              </div>
              <div>
                <Typography style={{ fontSize }}>
                  Дата: {formatDate(new Date(data.scheduleByDate.date))}
                </Typography>
                <Typography style={{ fontSize }}>
                  Автомобил: {data.scheduleByDate.carReg}
                </Typography>
              </div>
            </Toolbar>
          )}
        </Grid>
        <Grid item>
          <Toolbar>
            <Grid container>
              <Grid item>
                <Button
                  style={{
                    ...buttonStyle,
                    paddingLeft: '10px',
                    paddingRight: '10px',
                    backgroundColor: !notifications.seen && '#a5daff',
                  }}
                  variant="contained"
                  onClick={() =>
                    dispatch(setNotifications({ ...notifications, open: true, seen: true }))
                  }
                >
                  {notifications.list.length}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  style={buttonStyle}
                  variant="contained"
                  onClick={() => dispatch(setUsedMaterialsDialog({ open: true }))}
                >
                  Влож. мат.
                </Button>
              </Grid>
              <Grid item>
                <Button
                  style={buttonStyle}
                  variant="contained"
                  onClick={() =>
                    dispatch(
                      setMaterialsDialog({ open: true, dataSet: 'materials', checkout: false })
                    )
                  }
                >
                  Материали
                </Button>
              </Grid>
              <Grid item>
                <Button
                  style={buttonStyle}
                  variant="contained"
                  onClick={() => dispatch(setMaterialSellDialog({ open: true, readonly: true }))}
                >
                  Устройства за продажба
                </Button>
              </Grid>
              <Grid item>
                <Button
                  style={buttonStyle}
                  variant="contained"
                  onClick={() =>
                    dispatch(setMaterialsDialog({ open: true, dataSet: 'tools', checkout: false }))
                  }
                >
                  Инструменти
                </Button>
              </Grid>
              <Grid item>
                <NotificationButton buttonStyle={buttonStyle} />
              </Grid>
              <Grid item>
                <Button
                  style={buttonStyle}
                  variant="contained"
                  onClick={() =>
                    dispatch(
                      setReturnMaterialsDialog({
                        open: true,
                      })
                    )
                  }
                >
                  Върни в склад
                </Button>
              </Grid>
              <Grid item>
                <Button
                  style={buttonStyle}
                  variant="contained"
                  onClick={refreshAppData}
                  disabled={!data.loaded || appDataLoading}
                >
                  <Refresh style={{ fontSize }} />
                </Button>
              </Grid>
              <Grid item>
                <Button
                  style={buttonStyle}
                  variant="contained"
                  onClick={() => {
                    dispatch(getLogoutUrlRequest({ settings }));
                  }}
                  disabled={!data.loaded}
                >
                  <ExitToApp style={{ fontSize }} />
                </Button>
              </Grid>
              {chatConfigured && (
                <Grid item>
                  <GeneralChatButton style={buttonStyle} disabled={!data.loaded} />
                </Grid>
              )}
              {showTaskDetails.open && (
                <Button
                  fullWidth
                  onClick={() => dispatch(setShowTaskDetails({ open: false, taskId: null }))}
                  variant="contained"
                  style={buttonStyle}
                >
                  <ArrowBack />
                  Задачи
                </Button>
              )}
            </Grid>
          </Toolbar>
        </Grid>
      </Grid>
    </AppBar>
  );
};
